/**
 * Publishing quality profile for the MediTalk (LiveKit) provider.
 *
 * This is the LiveKit equivalent of `vonagePublisherQuality.js`. That file stays
 * exactly as it is and keeps serving the Vonage path; nothing here is imported
 * by the OpenTok code.
 *
 * Why capped: drivers run this inside the delivery app on metered cellular.
 * Uncapped simulcast publishes up to 720p in three layers, which burns data and
 * battery for detail nobody can resolve in a phone-sized tile. 540p at ~700kbps
 * looks the same in this UI.
 */

export const MEDITALK_AUDIO_CAPTURE_OPTIONS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

/**
 * Per-role publishing defaults.
 *
 * The driver is moving — smoothness beats sharpness, so frames are held and
 * resolution is allowed to drop (`maintain-framerate`). The pharmacist is
 * stationary at a desk and benefits from the opposite trade.
 */
export const MEDITALK_PUBLISH_DEFAULTS = {
  driver: {
    resolution: { width: 960, height: 540, frameRate: 24 },
    maxBitrate: 700000,
    maxFramerate: 24,
    degradationPreference: 'maintain-framerate',
  },
  pharmacist: {
    resolution: { width: 960, height: 540, frameRate: 24 },
    maxBitrate: 700000,
    maxFramerate: 24,
    degradationPreference: 'maintain-resolution',
  },
};

/** Fallback profile for a connection the browser already tells us is poor. */
export const MEDITALK_SLOW_CONNECTION_PROFILE = {
  resolution: { width: 640, height: 360, frameRate: 20 },
  maxBitrate: 300000,
  maxFramerate: 20,
};

/**
 * Same signal the Vonage profile uses, duplicated deliberately so this module
 * has no dependency on the Vonage one (either provider can be deleted later
 * without touching the other).
 */
export const isSlowConnection = () => {
  if (typeof navigator === 'undefined') return false;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return Boolean(
    connection?.saveData ||
    connection?.effectiveType === 'slow-2g' ||
    connection?.effectiveType === '2g' ||
    connection?.effectiveType === '3g'
  );
};

/**
 * Resolve the publish profile for a role, degraded when the browser reports a
 * slow/metered link. Degradation preference is kept from the role profile — a
 * driver on 3G still wants smooth motion over a sharp still.
 */
export const getMeditalkPublishProfile = (role = 'driver') => {
  const base = MEDITALK_PUBLISH_DEFAULTS[role] || MEDITALK_PUBLISH_DEFAULTS.driver;
  if (!isSlowConnection()) return base;
  return {
    ...base,
    resolution: MEDITALK_SLOW_CONNECTION_PROFILE.resolution,
    maxBitrate: MEDITALK_SLOW_CONNECTION_PROFILE.maxBitrate,
    maxFramerate: MEDITALK_SLOW_CONNECTION_PROFILE.maxFramerate,
  };
};
